import { nextRandom } from '../rng/rng';

// Numenera's core rules change (confirmed directly by Matt, not from a card
// or rulebook - see docs/expansion-audit.md): "Numenera uses the same rules
// as the base game but with one big change. The XP that are earned come in
// six colors. A player can discard one XP on their turn to get an effect
// based on which color they discarded. There are 9 of each color." This
// REPLACES the plain numeric XP pool entirely - hero-leveling costs and
// every "discard N XP" card effect are paid with colored tokens (any
// color), tracked here alongside the existing plain `player.xp` number
// (kept in sync as a derived total so the dozens of existing `player.xp`
// reads throughout the codebase - leveling costs, XP-gated abilities,
// scoring - keep working unchanged).
export const XP_TOKEN_COLORS = ['blue', 'gray', 'green', 'orange', 'red', 'yellow'];
export const XP_TOKENS_PER_COLOR = 9;

// Each color's spend effect, per the reference image Matt provided.
export const XP_TOKEN_EFFECTS = {
  blue: 'Draw 1 card.',
  gray: 'Destroy 1 card.',
  green: 'Add Magic Attack +1.',
  orange: 'Gain 2 gold.',
  red: 'Add Physical Attack +1.',
  yellow: 'Add Light +1.',
};

// Matt: "It would be nice if the colored XP count... actually use[d] the
// shape of the X[P] pieces since we have that SVG, were there instead of
// just a circle with the color." The one shared source of truth for each
// color's actual hex value - PlayerDock.vue's own xp-token pips and
// XpTokenChoicePanel.vue's level-up discard picker both need this as a JS
// value (ShardIcon's color prop), not just a CSS class.
export const XP_TOKEN_HEX_COLORS = {
  blue: '#5a9fd4', gray: '#a7a7a7', green: '#6fbf6f', orange: '#e08a3c', red: '#d4605a', yellow: '#e0c94a',
};

export function createInitialXpTokenSupply() {
  return Object.fromEntries(XP_TOKEN_COLORS.map((c) => [c, XP_TOKENS_PER_COLOR]));
}

export function createEmptyXpTokens() {
  return Object.fromEntries(XP_TOKEN_COLORS.map((c) => [c, 0]));
}

// Draws 1 random token from the shared supply, weighted by how many of each
// color remain (a true "draw blind from a bag of 54 tokens," not a uniform
// pick among the up-to-6 colors still available). Returns [color, nextState]
// - color is null if the supply is fully exhausted (all 54 already drawn).
export function drawXpTokenColor(state) {
  const supply = state.village.xpTokenSupply;
  const total = XP_TOKEN_COLORS.reduce((sum, c) => sum + supply[c], 0);
  if (!total) return [null, state];
  const [rand, nextRngState] = nextRandom(state.meta.rngState);
  let pick = Math.floor(rand * total);
  let color = null;
  for (const c of XP_TOKEN_COLORS) {
    if (pick < supply[c]) { color = c; break; }
    pick -= supply[c];
  }
  const nextState = {
    ...state,
    meta: { ...state.meta, rngState: nextRngState },
    village: { ...state.village, xpTokenSupply: { ...supply, [color]: supply[color] - 1 } },
  };
  return [color, nextState];
}

// Returns 1 token of the given color to the shared supply.
export function returnXpTokenColor(state, color) {
  const supply = state.village.xpTokenSupply;
  return { ...state, village: { ...state.village, xpTokenSupply: { ...supply, [color]: supply[color] + 1 } } };
}
